/**
 * CompaniesTab - Trucking companies database with CRUD
 * Extracted from TrainingView.jsx
 */

import { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Building2, Plus, Pencil, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CompaniesTab({ companies, setCompanies }) {
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [showEditCompany, setShowEditCompany] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({
    name: '', dot_number: '', mc_number: '', email: '', phone: '',
    street: '', city: '', state: '', zip_code: '', notes: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const resetCompanyForm = () => {
    setCompanyForm({
      name: '', dot_number: '', mc_number: '', email: '', phone: '',
      street: '', city: '', state: '', zip_code: '', notes: ''
    });
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const uploadLogo = async (companyId) => {
    if (!logoFile) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);
      await axios.post(`${API}/training/companies/${companyId}/logo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    } catch (e) {
      console.error('Logo upload failed:', e);
    } finally {
      setUploadingLogo(false);
    }
  };

  const addCompany = async () => {
    if (!companyForm.name) {
      toast.error('Company name is required');
      return;
    }
    try {
      const res = await axios.post(`${API}/training/companies`, companyForm, { withCredentials: true });
      const newCompanyId = res.data.company_id;
      if (logoFile && newCompanyId) {
        await uploadLogo(newCompanyId);
      }
      toast.success('Company added!');
      setShowAddCompany(false);
      resetCompanyForm();
      const companiesRes = await axios.get(`${API}/training/companies`, { withCredentials: true });
      setCompanies(companiesRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add company');
    }
  };

  const openEditCompany = (company) => {
    setEditingCompany(company);
    setCompanyForm({
      name: company.name || '',
      dot_number: company.dot_number || '',
      mc_number: company.mc_number || '',
      email: company.email || '',
      phone: company.phone || '',
      street: company.street || '',
      city: company.city || '',
      state: company.state || '',
      zip_code: company.zip_code || '',
      notes: company.notes || ''
    });
    if (company.logo_data) {
      setLogoPreview(`${API}/training/companies/${company.company_id}/logo`);
    } else {
      setLogoPreview(null);
    }
    setLogoFile(null);
    setShowEditCompany(true);
  };

  const updateCompany = async () => {
    if (!editingCompany) return;
    try {
      await axios.put(`${API}/training/companies/${editingCompany.company_id}`, companyForm, { withCredentials: true });
      if (logoFile) {
        await uploadLogo(editingCompany.company_id);
      }
      toast.success('Company updated!');
      setShowEditCompany(false);
      setEditingCompany(null);
      resetCompanyForm();
      const res = await axios.get(`${API}/training/companies`, { withCredentials: true });
      setCompanies(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update company');
    }
  };

  const deleteCompany = async (companyId) => {
    if (!window.confirm('Delete this company?')) return;
    try {
      await axios.delete(`${API}/training/companies/${companyId}`, { withCredentials: true });
      toast.success('Company deleted!');
      const safeCompanies = Array.isArray(companies) ? companies : [];
      setCompanies(safeCompanies.filter(c => c.company_id !== companyId));
    } catch (e) {
      toast.error('Failed to delete company');
    }
  };

  const CompanyFormFields = () => (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div>
        <label className="text-sm text-muted-foreground">Company Name *</label>
        <input
          type="text"
          value={companyForm.name}
          onChange={e => setCompanyForm({...companyForm, name: e.target.value})}
          className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
          placeholder="ABC Trucking Inc."
          data-testid="company-name-input"
        />
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-sm text-muted-foreground">DOT Number</label>
          <input
            type="text"
            value={companyForm.dot_number}
            onChange={e => setCompanyForm({...companyForm, dot_number: e.target.value})}
            className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-blue-500 outline-none font-mono"
            placeholder="1234567"
            data-testid="company-dot-input"
          />
        </div>
        <div>
          <label className="text-sm text-muted-foreground">MC Number</label>
          <input
            type="text"
            value={companyForm.mc_number}
            onChange={e => setCompanyForm({...companyForm, mc_number: e.target.value})}
            className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-green-500 outline-none font-mono"
            placeholder="987654"
            data-testid="company-mc-input"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-sm text-muted-foreground">Email</label>
          <input
            type="email"
            value={companyForm.email}
            onChange={e => setCompanyForm({...companyForm, email: e.target.value})}
            className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
            placeholder="dispatch@company.com"
          />
        </div>
        <div>
          <label className="text-sm text-muted-foreground">Phone</label>
          <input
            type="tel"
            value={companyForm.phone}
            onChange={e => setCompanyForm({...companyForm, phone: e.target.value})}
            className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
            placeholder="+1 555-123-4567"
          />
        </div>
      </div>
      
      <div>
        <label className="text-sm text-muted-foreground">Street Address</label>
        <input
          type="text"
          value={companyForm.street}
          onChange={e => setCompanyForm({...companyForm, street: e.target.value})}
          className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
          placeholder="123 Main Street"
        />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-sm text-muted-foreground">City</label>
          <input
            type="text"
            value={companyForm.city}
            onChange={e => setCompanyForm({...companyForm, city: e.target.value})}
            className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
            placeholder="Chicago"
          />
        </div>
        <div>
          <label className="text-sm text-muted-foreground">State</label>
          <input
            type="text"
            value={companyForm.state}
            onChange={e => setCompanyForm({...companyForm, state: e.target.value})}
            className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
            placeholder="IL"
          />
        </div>
        <div>
          <label className="text-sm text-muted-foreground">ZIP Code</label>
          <input
            type="text"
            value={companyForm.zip_code}
            onChange={e => setCompanyForm({...companyForm, zip_code: e.target.value})}
            className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
            placeholder="60601"
          />
        </div>
      </div>
      
      <div>
        <label className="text-sm text-muted-foreground">Company Logo <span className="text-purple-400">(for PDF exports - auto-resized)</span></label>
        <div className="mt-2 flex items-center gap-4">
          {logoPreview ? (
            <div className="w-20 h-20 rounded-xl bg-secondary border border-border flex items-center justify-center overflow-hidden">
              <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
            </div>
          ) : (
            <div className="w-20 h-20 rounded-xl bg-secondary border-2 border-dashed border-border flex items-center justify-center">
              <Building2 className="w-8 h-8 text-muted-foreground" />
            </div>
          )}
          <div className="flex-1">
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoSelect}
              className="hidden"
              id="logo-upload"
              data-testid="company-logo-input"
            />
            <label
              htmlFor="logo-upload"
              className="inline-block px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg cursor-pointer transition-colors"
            >
              {logoFile ? 'Change Image' : (logoPreview ? 'Replace Logo' : 'Upload Logo')}
            </label>
            {logoFile && <p className="text-xs text-muted-foreground mt-1">{logoFile.name}</p>}
            <p className="text-xs text-muted-foreground mt-1">PNG, JPG up to 5MB • Auto-resized for PDF</p>
          </div>
        </div>
      </div>
      
      <div>
        <label className="text-sm text-muted-foreground">Notes</label>
        <textarea
          value={companyForm.notes}
          onChange={e => setCompanyForm({...companyForm, notes: e.target.value})}
          className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
          rows={2}
          placeholder="Additional notes about this company..."
        />
      </div>
    </div>
  );

  return (
    <>
      <div className="bg-card/60 backdrop-blur-xl rounded-2xl border border-border/30 overflow-hidden">
        <div className="p-4 border-b border-border/30 flex items-center justify-between">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Building2 className="w-5 h-5 text-purple-400" />
            Trucking Companies Database
          </h3>
          <button
            onClick={() => setShowAddCompany(true)}
            className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-medium flex items-center gap-2 transition-colors"
            data-testid="add-company-btn"
          >
            <Plus className="w-4 h-4" />
            Add Company
          </button>
        </div>
        <div className="divide-y divide-border/30">
          {(!Array.isArray(companies) || companies.length === 0) ? (
            <div className="p-8 text-center text-muted-foreground">
              <Building2 className="w-12 h-12 mx-auto mb-3 text-purple-400/50" />
              <p className="font-medium">No companies yet</p>
              <p className="text-sm mt-2">Click "Add Company" to start building your trucking companies database.</p>
              <p className="text-xs mt-1 text-purple-400">Companies are used for Random Driver assignments and PDF exports with logos.</p>
            </div>
          ) : (
            companies.map((c) => (
              <div key={c.company_id} className="p-4 hover:bg-accent/5 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl bg-purple-500/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {c.logo_data ? (
                      <img src={`${API}/training/companies/${c.company_id}/logo`} alt={c.name} className="w-full h-full object-contain" />
                    ) : (
                      <Building2 className="w-8 h-8 text-purple-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <p className="font-bold text-lg">{c.name}</p>
                      {c.dot_number && (
                        <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-mono rounded">DOT# {c.dot_number}</span>
                      )}
                      {c.mc_number && (
                        <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-mono rounded">MC# {c.mc_number}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
                      {c.email && <span className="flex items-center gap-1">📧 {c.email}</span>}
                      {c.phone && <span className="flex items-center gap-1">📞 {c.phone}</span>}
                    </div>
                    {(c.street || c.city || c.state) && (
                      <p className="text-sm text-muted-foreground mt-1">
                        📍 {[c.street, c.city, c.state, c.zip_code].filter(Boolean).join(', ')}
                      </p>
                    )}
                    {c.notes && <p className="text-sm text-muted-foreground mt-1 italic">"{c.notes}"</p>}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button 
                      onClick={() => openEditCompany(c)} 
                      className="p-2 hover:bg-blue-500/20 rounded-lg text-blue-400"
                      data-testid={`edit-company-${c.company_id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => deleteCompany(c.company_id)} 
                      className="p-2 hover:bg-red-500/20 rounded-lg text-red-400"
                      data-testid={`delete-company-${c.company_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        {Array.isArray(companies) && companies.length > 0 && (
          <div className="p-3 bg-purple-500/10 border-t border-purple-500/30 text-center">
            <p className="text-xs text-purple-400">{companies.length} trucking {companies.length === 1 ? 'company' : 'companies'} in database</p>
          </div>
        )}
      </div>

      {/* Add Company Modal */}
      {showAddCompany && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 overflow-y-auto py-4" onClick={() => { setShowAddCompany(false); resetCompanyForm(); }}>
          <div className="bg-card rounded-2xl p-6 max-w-lg w-full mx-4 my-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-400" />
              Add Trucking Company
            </h3>
            <CompanyFormFields />
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowAddCompany(false); resetCompanyForm(); }} className="flex-1 py-3 bg-secondary rounded-xl font-medium">Cancel</button>
              <button onClick={addCompany} disabled={!companyForm.name || uploadingLogo} className="flex-1 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-bold disabled:opacity-50" data-testid="save-company-btn">
                {uploadingLogo ? 'Uploading...' : 'Add Company'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Company Modal */}
      {showEditCompany && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 overflow-y-auto py-4" onClick={() => { setShowEditCompany(false); setEditingCompany(null); }}>
          <div className="bg-card rounded-2xl p-6 max-w-lg w-full mx-4 my-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-400" />
              Edit Trucking Company
            </h3>
            <CompanyFormFields />
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowEditCompany(false); setEditingCompany(null); resetCompanyForm(); }} className="flex-1 py-3 bg-secondary rounded-xl font-medium">Cancel</button>
              <button onClick={updateCompany} disabled={!companyForm.name || uploadingLogo} className="flex-1 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-bold disabled:opacity-50" data-testid="update-company-btn">
                {uploadingLogo ? 'Uploading...' : 'Update Company'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
